import { Presence } from 'phoenix'
import { channel } from '@App/Socket'
import Vue from 'vue'
import { meta } from '@App/Meta'
import { debounce } from '@Shared/Functions'
import axios from 'axios'

let storeGlobal = null

const loadUsersProfiles = debounce(() => {
    storeGlobal.dispatch('perfil/loadDebouncedUsersProfiles')
}, 100)

const loadContactsProfiles = debounce(() => {
    storeGlobal.dispatch('perfil/loadDebouncedContactsProfiles')
}, 100)

export function atualizarStore(store) {
    storeGlobal = store
}

export function perfil(canal) {
    let perfil = storeGlobal.getters['perfil/perfil'](canal)
    if (perfil) return perfil

    if (storeGlobal.state.perfil.carregando[canal]) {
        return { nome: __('Carregando...') }
    }

    if (canal.indexOf('core_users:') !== -1) {
        storeGlobal.dispatch('perfil/loadUserProfile', canal.replace('core_users:', ''))
    } else if (canal.indexOf('crm_contacts:') !== -1) {
        storeGlobal.dispatch('perfil/loadContactProfile', canal.replace('crm_contacts:', ''))
    }

    return { nome: __('Carregando...') }
}

/**
 * Aguarda o perfil do canal especificado. Caso o perfil já esteja em memória, a promise é resolvida imediatamente.
 *
 * @param {string} canal
 * @return {Promise<any>}
 */
export async function aguardarPerfil(canal) {
    let perfil = storeGlobal.getters['perfil/perfil'](canal)
    if (perfil) return perfil

    if (canal.indexOf('core_users:') !== -1) {
        await storeGlobal.dispatch('perfil/loadUserProfile', canal.replace('core_users:', ''))
    } else if (canal.indexOf('crm_contacts:') !== -1) {
        await storeGlobal.dispatch('perfil/loadContactProfile', canal.replace('crm_contacts:', ''))
    }

    return storeGlobal.getters['perfil/perfil'](canal)
}

export function userProfile(user_id) {
    let perfil = storeGlobal.getters['perfil/userProfile'](user_id)
    if (perfil) return perfil

    if (storeGlobal.state.perfil.carregando['core_users:' + user_id]) {
        return { nome: __('Carregando...') }
    }

    storeGlobal.dispatch('perfil/loadUserProfile', user_id)

    return { nome: __('Carregando...') }
}

export function contactProfile(contact_id) {
    let perfil = storeGlobal.getters['perfil/contactProfile'](contact_id)
    if (perfil) return perfil

    if (storeGlobal.state.perfil.carregando['crm_contacts:' + contact_id]) {
        return { name: __('Carregando...') }
    }

    storeGlobal.dispatch('perfil/loadContactProfile', contact_id)

    return {}
}

export function contactConnectionStatus(contact_id) {
    let perfil = storeGlobal.getters['perfil/contactProfile'](contact_id)
    if (perfil) {
        if (perfil.online !== null) return perfil.online

        let chave = 'crm_contacts:' + contact_id

        storeGlobal.commit('perfil/atualizarStatus', { chave, online: false })

        let contactPresenceChannel = channel('presence:' + chave)
        let presence = new Presence(contactPresenceChannel)

        presence.onSync(() => {
            let metas = presence
                .list()
                .flatMap(conn => conn.metas)
                .map(meta => meta.canal)

            if (metas.indexOf('anonymous_contact') !== -1) {
                storeGlobal.commit('perfil/atualizarStatus', { chave, online: true })
            } else {
                storeGlobal.commit('perfil/atualizarStatus', { chave, online: false })
            }
        })

        contactPresenceChannel.join()
    }
}

export const Perfil = {
    namespaced: true,
    state: () => {
        return {
            /**
             * Perfis dos usuários e contatos que já foram carregados. A chave desse mapa é no formato
             * "core_users:<id>" para usuários e "crm_contacts:<id>" para contatos.
             */
            perfis: {},

            /**
             * Mapa no mesmo formato dos perfis para impedir o carregamentos simultâneos do mesmo perfil.
             */
            carregando: {},

            debouncedUsersIds: [],
            debouncedContactsIds: [],
        }
    },
    getters: {
        perfil: state => canal => {
            return state.perfis[canal]
        },
        userProfile: state => user_id => {
            return state.perfis['core_users:' + user_id]
        },
        contactProfile: state => contact_id => {
            return state.perfis['crm_contacts:' + contact_id]
        },
    },
    mutations: {
        atualizarCarregando(state, chave) {
            state.carregando[chave] = true
        },
        atualizarPerfil(state, { chave, perfil }) {
            state.carregando[chave] = false
            Vue.set(state.perfis, chave, perfil)
        },

        atualizarChatDisponibilidade(state, { chave, chat_disponibilidade }) {
            if (state.perfis[chave].chat_disponibilidade !== undefined) {
                state.perfis[chave].chat_disponibilidade = chat_disponibilidade
            } else {
                Vue.set(state.perfis[chave], 'chat_disponibilidade', chat_disponibilidade)
            }
        },

        atualizarStatus(state, { chave, online }) {
            if (state.perfis[chave].online !== undefined) {
                state.perfis[chave].online = online
            } else {
                Vue.set(state.perfis[chave], 'online', online)
            }
        },

        pushDebouncedUserId(state, userId) {
            state.debouncedUsersIds.push(userId)
        },
        clearDebouncedUserIds(state) {
            state.debouncedUsersIds = []
        },

        pushDebouncedContactId(state, contactId) {
            state.debouncedContactsIds.push(contactId)
        },
        clearDebouncedContactsIds(state) {
            state.debouncedContactsIds = []
        },
    },
    actions: {
        async loadUserProfile({ commit }, user_id) {
            let chave = 'core_users:' + user_id
            commit('atualizarCarregando', chave)

            commit('pushDebouncedUserId', user_id)
            loadUsersProfiles()
        },

        async loadDebouncedUsersProfiles({ commit, state }) {
            let usersIds = state.debouncedUsersIds
            commit('clearDebouncedUserIds')

            let { data } = await axios.get('/traits/profiles/users', { params: { users_ids: usersIds } })

            data.forEach(userProfile => {
                let chave = 'core_users:' + userProfile.id
                commit('atualizarPerfil', { chave, perfil: userProfile })

                // Se não estamos acessando em um ambiente autenticado, não devemos entrar no channel do usuário.
                if (!meta('user-id')) return

                let canalDoOutroUser = channel('presence:' + chave)
                let presence = new Presence(canalDoOutroUser)

                if (userProfile.id == meta('user-id')) return

                presence.onSync(() => {
                    let metas = presence
                        .list()
                        .flatMap(conn => conn.metas)
                        .map(meta => meta.canal)

                    if (metas.indexOf(chave) !== -1) {
                        commit('atualizarStatus', { chave, online: true })
                    } else {
                        commit('atualizarStatus', { chave, online: false })
                    }
                })

                canalDoOutroUser.on('broadcast:event', data => {
                    if (data.event == 'update:chat_disponibilidade') {
                        let chat_disponibilidade = data.data.chat_disponibilidade
                        commit('atualizarChatDisponibilidade', { chave, chat_disponibilidade })
                    }
                })

                canalDoOutroUser.join()
            })
        },

        async loadContactProfile({ commit }, contact_id) {
            commit('atualizarCarregando', 'crm_contacts:' + contact_id)
            commit('pushDebouncedContactId', contact_id)
            loadContactsProfiles()
        },

        async reloadContactProfile({ commit }, contact_id) {
            commit('atualizarCarregando', 'crm_contacts:' + contact_id)
            commit('pushDebouncedContactId', contact_id)
            loadContactsProfiles()
        },

        async loadDebouncedContactsProfiles({ commit, state }) {
            let contactsIds = state.debouncedContactsIds
            commit('clearDebouncedContactsIds')

            let { data } = await axios.get('/traits/profiles/contacts/', { params: { contacts_ids: contactsIds } })

            data.forEach(contactProfile => {
                commit('atualizarPerfil', {
                    chave: 'crm_contacts:' + contactProfile.id,
                    perfil: { ...contactProfile, online: null },
                })
            })
        },
    },
}
